"use client";
import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useContactModal } from '../../../../contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const INK = '#080400';
const PURPLE = '#3730a3';
const LIME = '#ceff1a';
const CARD = '#fbfdfd';

interface ServiceItem {
    n: string;
    title: string;
    icon: React.ReactNode;
    desc: string;
    href: string;
}

const services: ServiceItem[] = [
    {
        n: '01',
        title: 'Technical SEO',
        href: '/services/search-visibility/technical-seo',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="6" stroke="currentColor" strokeWidth="2" />
                <path d="M16 4v3.5M16 24.5V28M4 16h3.5M24.5 16H28M7.6 7.6l2.5 2.5M21.9 21.9l2.5 2.5M24.4 7.6l-2.5 2.5M10.1 21.9l-2.5 2.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        ),
        desc: 'Improve site performance, crawlability, Core Web Vitals, indexing, site architecture, and structured data to create a solid technical foundation for growth.',
    },
    {
        n: '02',
        title: 'Local SEO',
        href: '/services/search-visibility/local-seo',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <path d="M16 29s10-9.5 10-16.5A10 10 0 1 0 6 12.5C6 19.5 16 29 16 29z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <circle cx="16" cy="12.5" r="3.5" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
        desc: 'Dominate local search through Google Business Profile optimisation, local landing pages, review strategies, and location-focused SEO.',
    },
    {
        n: '03',
        title: 'SEO Content Strategy',
        href: '/services/search-visibility/seo-content-strategy',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <rect x="5" y="7" width="15" height="20" rx="2.5" stroke="currentColor" strokeWidth="2" />
                <rect x="13" y="3" width="15" height="20" rx="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
                <path d="M17 9h7M17 13h7M17 17h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
        ),
        desc: 'Create content ecosystems designed around topical authority, user intent, and measurable business outcomes.',
    },
    {
        n: '04',
        title: 'AI SEO / GEO',
        href: '/services/search-visibility/ai-seo',
        icon: (
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <path d="M16 4l3 8 8 3-8 3-3 8-3-8-8-3 8-3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
        desc: 'AEO, GEO, LLMO, and Search Experience Optimisation to help your brand stay visible across AI Overviews, ChatGPT, Gemini, and emerging search experiences.',
    },
];

function ArrowIcon({ className = '' }: { className?: string }) {
    return (
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" className={className}>
            <path d="M2 6.5h9M7.5 2.5l4 4-4 4" stroke={PURPLE} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function ServiceCard({ service, index }: { service: ServiceItem; index: number }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            whileHover={{ y: -6 }}
            className="group relative rounded-3xl transition-colors duration-300"
            style={{
                background: CARD,
                border: '1.5px solid rgba(0,0,0,0.08)',
            }}
        >
            <Link href={service.href} className="block h-full p-8 flex flex-col justify-between gap-8" style={{ minHeight: 280 }}>
                <div className="flex items-start justify-between">
                    <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105"
                        style={{
                            background: 'rgba(55,48,163,0.08)',
                            border: '1px solid rgba(55,48,163,0.15)',
                            color: PURPLE,
                        }}
                    >
                        {service.icon}
                    </div>
                    <span
                        className="text-xs font-bold"
                        style={{ fontFamily: CLASH, color: 'rgba(8,4,0,0.2)' }}
                    >
                        {service.n}
                    </span>
                </div>

                <div className="flex flex-col gap-3">
                    <h3
                        className="text-xl md:text-2xl font-bold leading-snug"
                        style={{ fontFamily: CLASH, color: PURPLE, letterSpacing: '0.02em' }}
                    >
                        {service.title}
                    </h3>
                    <p
                        className="text-sm leading-relaxed"
                        style={{ fontFamily: MANROPE, color: 'rgba(8,4,0,0.6)', letterSpacing: '0.02em' }}
                    >
                        {service.desc}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                        <span
                            className="text-xs font-semibold tracking-[0.15em] uppercase"
                            style={{ color: PURPLE, fontFamily: CLASH }}
                        >
                            Learn more
                        </span>
                        <ArrowIcon className="transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}

export default function SEOServices() {
    const { openModal } = useContactModal();

    return (
        <section
            className="relative overflow-hidden py-24 px-6 md:px-16 lg:px-24"
            style={{ background: INK }}
        >
            <div className="max-w-[1400px] mx-auto flex flex-col gap-16">

                {/* Header */}
                <div className="flex flex-col gap-4 max-w-2xl">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: LIME, fontFamily: CLASH }}
                    >
                        Our Services
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-4xl md:text-6xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: CLASH, color: '#fbfdfd' }}
                    >
                        OUR SEO SERVICES
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.18 }}
                        className="text-base md:text-lg leading-relaxed"
                        style={{ fontFamily: MANROPE, color: 'rgba(251,253,253,0.6)' }}
                    >
                        Four disciplines working together to make your brand impossible to miss — technically, locally, and across AI-powered search.
                    </motion.p>
                </div>

                {/* Card grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {services.map((s, i) => (
                        <ServiceCard key={s.title} service={s} index={i} />
                    ))}
                </div>

                {/* CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center"
                >
                    <button
                        onClick={openModal}
                        className="inline-flex items-center justify-center px-10 py-5 rounded-full font-semibold text-lg transition-transform duration-300 hover:scale-105"
                        style={{
                            background: PURPLE,
                            color: '#ffffff',
                            fontFamily: CLASH,
                            letterSpacing: '0.08em',
                        }}
                    >
                        Get Your Free SEO Audit →
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
